import React from 'react';
import { cn } from "@/lib/utils";
import { format } from 'date-fns';
import { Users, Check, Ban, Lock } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { getInitials } from '@/utils/commonUtils';
import { useUserStore } from '@/store/userStore';
import { useSettingsStore } from '@/store/api/settingsStore';
import { ScheduleSessionSurface } from '@/components/schedule/ScheduleSessionSurface';

interface WeeklyTimeSlotProps {
  date: Date;
  time: string;
  classInfo: any;
  overlappingClasses?: any[];
  isAdmin: boolean;
  onAddClass: (date: Date, time: string) => void;
  onHandleClassClick: (classInfo: any, date: Date) => void;
  isClassBooked?: (classId: string, date: Date, time: string) => boolean;
  isFirstSlotOfClass?: boolean;
  timeSlotHeight: number;
  classDurationInSlots?: number;
  isMobile?: boolean;
  isRecentlyBooked?: boolean;
  isRealtimeModified?: boolean;
  bookingAdvanceDays?: number;
  compact?: boolean;
}

export const WeeklyTimeSlot: React.FC<WeeklyTimeSlotProps> = ({
  date,
  time,
  classInfo,
  overlappingClasses = [],
  isAdmin,
  onAddClass,
  onHandleClassClick,
  isClassBooked = () => false,
  isFirstSlotOfClass = true,
  timeSlotHeight,
  classDurationInSlots = 1,
  isMobile = false,
  isRecentlyBooked = false,
  isRealtimeModified = false,
  bookingAdvanceDays = 0,
  compact = false
}) => {
  const totalHeight = timeSlotHeight * classDurationInSlots;
  const { t } = useTranslation(["schedule", "bookingui"]);
  const { role } = useUserStore();
  const { settings } = useSettingsStore();
  const hideAvailableSlots = settings?.schedule?.hideAvailableSlotsForClients && !isAdmin;

  const hasMultipleClasses = overlappingClasses.length > 1;
  const effectiveClassInfo = classInfo || (overlappingClasses.length === 1 ? overlappingClasses[0] : null);
  const hasAnyClass = effectiveClassInfo || hasMultipleClasses;

  const getDisplayName = (name: string, compact?: boolean): string => {
    if (compact) return name;
    if (!isMobile) return name;
    if (name.length < 10) return name;
    const words = name.trim().split(/\s+/);
    return words.length >= 2 ? getInitials(name) : name;
  };

  const handleAddClass = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddClass(date, time);
  };

  const isDateBeyondLimit = (() => {
    if (!bookingAdvanceDays || bookingAdvanceDays === 0) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + bookingAdvanceDays);
    maxDate.setHours(23, 59, 59, 999);
    return date > maxDate;
  })();

  if (hasAnyClass && !isFirstSlotOfClass) {
    return (
      <div
        className={cn(
          'border-b border-border/50'
        )}
        style={{ height: `${timeSlotHeight}px` }}
        data-date={format(date, 'yyyy-MM-dd')}
        data-time={time}
      />
    );
  }

  const getAvailableSpots = (ci: any) => {
    if (typeof ci.spots_available === 'number') return ci.spots_available;
    if (typeof ci.booked_count === 'number') return (ci.capacity || 0) - ci.booked_count;
    return ci.capacity || 0;
  };

  const getCapacityColorClass = (ci: any) => {
    const spots = getAvailableSpots(ci);
    if (!ci || !ci.capacity) {
      return 'schedule-capacity-full';
    }
    const percentage = ci.capacity === 0 ? 0 : (spots / ci.capacity) * 100;
    if (percentage >= 60) return 'schedule-capacity-available';
    if (percentage >= 40) return 'schedule-capacity-limited';
    if (percentage > 0) return 'schedule-capacity-nearly-full';
    return 'schedule-capacity-full';
  };

  const renderClassCard = (ci: any, isOverlapCompact: boolean) => {
    const booked = isClassBooked(ci.id, date, time);
    const spots = getAvailableSpots(ci);
    const occupiedSpots = Math.max(0, (ci.capacity || 0) - spots);
    const blocked = !isAdmin && (booked || spots <= 0 || isDateBeyondLimit);

    return (
      <ScheduleSessionSurface key={ci.id}>
      <div
        key={ci.id}
        className={cn(
          'relative text-xs rounded flex flex-col touch-manipulation min-w-0 overflow-hidden',
          isOverlapCompact ? 'flex-1' : 'h-full',
          isOverlapCompact || compact ? 'p-0.5 gap-0' : (isMobile ? 'p-0.5 gap-0.5' : 'p-1 gap-0.5'),
          booked
            ? 'schedule-capacity-booked'
            : isDateBeyondLimit && !isAdmin
              ? 'border-border bg-muted text-muted-foreground'
              : getCapacityColorClass(ci),
          blocked ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
        onClick={blocked ? undefined : () => onHandleClassClick(ci, date)}
      >
        <div className={cn(
          'schedule-class-name shrink-0 overflow-hidden font-semibold leading-tight',
          isOverlapCompact || compact
            ? (isMobile ? 'line-clamp-2 break-words text-[8px]' : 'line-clamp-2 text-[10px]')
            : (isMobile ? 'truncate text-[9px]' : 'truncate text-[11px]')
        )} title={`${ci.name} - ${ci.category}`}>
          {getDisplayName(ci.name, isOverlapCompact || compact)}
        </div>
        {!isOverlapCompact && (
          <div className={cn(
            'shrink-0 truncate leading-tight opacity-80',
            compact ? (isMobile ? 'text-[7px]' : 'text-[9px]') : (isMobile ? 'text-[7px]' : 'text-[9px]')
          )}>
            {getDisplayName(ci.category)}
          </div>
        )}
        {/* Numeric capacity badge: absolutely anchored to the bottom-right corner of the
            card so it never adds height, never overflows and never gets truncated,
            regardless of slot height (mobile / desktop / compact / multi-slot classes). */}
        {isAdmin ? (
          <span
            className={cn(
              'schedule-class-capacity absolute bottom-0 right-0.5 z-10 flex max-w-full items-center gap-0.5 overflow-hidden whitespace-nowrap leading-none',
              'text-muted-foreground',
              isOverlapCompact || compact ? 'text-[7px]' : (isMobile ? 'text-[8px]' : 'text-[10px]')
            )}
            title={t("quotaSlot.availableCapacity", { ns: "bookingui" })}
          >
            {!isMobile && !isOverlapCompact && !compact && <Users className="h-3 w-3 shrink-0" />}
            <span className="truncate">{occupiedSpots}/{ci.capacity}</span>
          </span>
        ) : (
          !booked && spots > 0 && !hideAvailableSlots && (
            <span
              className={cn(
                'schedule-class-capacity absolute bottom-0 right-0.5 z-10 flex max-w-full items-center overflow-hidden whitespace-nowrap leading-none font-medium',
                'text-muted-foreground',
                isOverlapCompact || compact ? 'text-[6px]' : (isMobile ? 'text-[8px]' : 'text-[10px]')
              )}
              title={t("quotaSlot.availableCapacity", { ns: "bookingui" })}
            >
              {spots}
            </span>
          )
        )}
        {/* Booked / full states stay centered and only render when no numeric badge is shown */}
        {!isAdmin && (booked || spots <= 0) && (
          <div className="schedule-class-status mt-auto flex shrink-0 items-end justify-center overflow-hidden leading-none">
            {booked ? (
              <div className="flex w-full min-w-0 items-center justify-center overflow-hidden">
                {isMobile ? (
                  <Check className={cn('shrink-0 font-bold text-[hsl(var(--schedule-booked,var(--schedule-available)))]', isOverlapCompact ? 'h-2 w-2' : 'h-2.5 w-2.5')} />
                ) : (
                  <div className={cn(
                    'flex min-w-0 max-w-full items-center justify-center gap-px overflow-hidden whitespace-nowrap font-bold text-[hsl(var(--schedule-booked,var(--schedule-available)))]',
                    isOverlapCompact || compact ? 'text-[6px]' : 'text-[10px]'
                  )}>
                    <Check className={cn('shrink-0', isOverlapCompact || compact ? 'h-1.5 w-1.5' : 'h-2 w-2')} />
                    <span className="truncate">{t("booked")}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex w-full min-w-0 items-center justify-center overflow-hidden">
                {isMobile ? (
                  <Lock className={cn('shrink-0 font-bold text-destructive', isOverlapCompact ? 'h-2 w-2' : 'h-2.5 w-2.5')} />
                ) : (
                  <span className={cn(
                    'block max-w-full truncate whitespace-nowrap font-bold text-destructive',
                    isOverlapCompact || compact ? 'text-[6px]' : 'text-[10px]'
                  )}>
                    {t("full")}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      </ScheduleSessionSurface>
    );
  };

  return (
    <div
      className={cn(
        'relative',
        isMobile ? 'p-0.5' : 'p-1',
        'border-b border-border/50',
        hasAnyClass
          ? 'bg-card'
          : 'hover:bg-muted/40',
        isRecentlyBooked && "ring-2 ring-green-500 ring-inset"
      )}
      style={hasAnyClass && classDurationInSlots > 1 ? {
        height: `${totalHeight}px`,
        zIndex: 10,
        flexShrink: 0
      } : {
        height: `${timeSlotHeight}px`,
        flexShrink: 0
      }}
      data-date={format(date, 'yyyy-MM-dd')}
      data-time={time}
    >
      {isRecentlyBooked && (
        <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center z-10 animate-pulse">
          <div className="bg-green-500 text-white rounded-full p-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        </div>
      )}
      {isRealtimeModified && !isRecentlyBooked && (
        <div className="absolute inset-0 ring-2 ring-blue-500 ring-inset animate-pulse pointer-events-none z-10" />
      )}
      {hasMultipleClasses ? (
        <div className="flex gap-px h-full">
          {overlappingClasses.map(ci => renderClassCard(ci, true))}
        </div>
      ) : effectiveClassInfo ? (
        renderClassCard(effectiveClassInfo, false)
      ) : (
        isAdmin && role !== 'coach' && (
          <button
            onClick={handleAddClass}
            className={cn(
              'w-full h-full flex items-center justify-center',
              'text-muted-foreground hover:text-primary',
              isMobile ? 'text-[8px]' : 'text-xs'
            )}
          >
            +
          </button>
        )
      )}
    </div>
  );
};
